/*
 *
 * BookingDetailActionsPopUp
 *
 * @version 1.0.0
 * @author Magarelli-Fabrizio
 * @since 19-Mar-2025
 */

import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'
import DatePicker from '@nexidigital/datepicker'
import MerchantModal from '@nexidigital/merchantmodal'
import MerchantTimePicker from '@nexidigital/merchanttimepicker'
import PortalCheckBox from '@nexidigital/portalcheckbox'
import PortalRadioBox from '@nexidigital/portalradiobox'
import PortalSelect from '@nexidigital/portalselect'
import PortalTextInput from '@nexidigital/portaltextinput'
import Toggle from 'Hoc/ToggleComponent'
import { joinDateAndTime, datePickerEternalMaxDate } from 'Utility/dateMethods'
import { momentLongDateFormat } from 'Utility/dateMethods'
import { getLanguageCalendar } from 'Utility/globalMethods'
import {
  formatNumberString,
  amountValidator,
  parseStringNumber,
  getDividedAmount,
  handleOnlyNumberKeyDown
} from 'Utility/numberMethods'
import { routesList } from 'Schemas/routesList'
import formattedMessage, { captions, messages } from './languageModule'
import './BookingDetailActionsPopUp.scss'

const BookingDetailActionsPopUp = ({ actions, isLoading, data, selectedBooking }) => {
  const formatDate = momentLongDateFormat()
  
  const intl = useIntl()
  const navigate = useNavigate()
  const status = selectedBooking?.status || ''
  const [noShowExceptionAccepted, setNoShowExceptionAccepted] = useState(false)
  const checkInFrom = moment(selectedBooking?.checkInFrom)
  const isNoShowOutside72HourWindow =
    data?.action === 'NO_SHOW' &&
    checkInFrom.isValid() &&
    moment.duration(checkInFrom.diff(moment())).abs().asHours() > 72
  const currency = selectedBooking?.currency || 'EUR'
  const selectList = [
    { value: 'BANK_TRANSFER', label: intl.formatMessage(captions.bankTransfer) },
    { value: 'CASH', label: intl.formatMessage(captions.cash) },
    { value: 'POS', label: intl.formatMessage(captions.pos) },
    { value: 'OTHER', label: intl.formatMessage(captions.other) }
  ]
  const noDateTimeList = [
    'CLONE',
    'CARD_SUBSTITUTION_LINK',
    'PAYMENT',
    'NO_SHOW',
    'DELAYED_CHARGE',
    'REFUND',
    'SEND_LINK'
  ]
  const noAmountList = ['CLONE', 'CARD_SUBSTITUTION_LINK', 'CHECKIN', 'CHECKOUT', 'CANCEL', 'SEND_LINK']
  const noDescriptionList = ['CLONE', 'CARD_SUBSTITUTION_LINK', 'SEND_LINK']
  const noRequiredList = ['CANCEL']

  const formik = useFormik({
    initialValues: {
      action: data?.action,
      date: moment().format(formatDate),
      time: moment(),
      amount: data.amount ? formatNumberString(getDividedAmount(Number(data?.amount), currency).toString()) : '',
      description: '',
      billType: status === 'AWAITING_FOR_CONFIRMATION' ? 'BILL_LATER' : 'BILL_NOW',
      externalPaymentChannel: '',
      externalPaymentChannelDetails: ''
    },
    validationSchema: Yup.object({
      date: Yup.string()
        .nullable()
        .test(
          'checkin-checkout-date-not-in-future',
          intl.formatMessage({
            id: 't.app.bookingManagement.createBookingPayByLink.validator.notFutureDateTime',
            defaultMessage: 'Date and time cannot be in the future'
          }),
          function (value) {
            const action = this?.parent?.action
            if (action !== 'CHECKIN' && action !== 'CHECKOUT') return true
            if (!value) return true

            const selectedDate = moment(value, formatDate, true)
            return selectedDate.isValid() ? selectedDate.isSameOrBefore(moment(), 'day') : true
          }
        )
        .test(
          'checkout-not-before-checkin-from',
          intl.formatMessage({
            id: 't.app.bookingManagement.createBookingPayByLink.validator.checkoutBeforeCheckinFrom',
            defaultMessage: 'Date and time must not be before check-in date'
          }),
          function (value) {
            const action = this?.parent?.action
            if (action !== 'CHECKOUT') return true

            const date = this?.parent?.date
            const time = this?.parent?.time
            const checkInFrom = selectedBooking?.checkInFrom

            if (!date || !value || !checkInFrom) return true

            const selectedDate = moment(date, formatDate)
            const selectedTime = moment(time)
            const checkInFromDateTime = moment(checkInFrom)

            if (!selectedDate.isValid() || !selectedTime.isValid() || !checkInFromDateTime.isValid()) return true

            const selectedDateTime = selectedDate
              .clone()
              .hour(selectedTime.hour())
              .minute(selectedTime.minute())
              .second(selectedTime.second())
              .millisecond(selectedTime.millisecond())
            return selectedDateTime.isSameOrAfter(checkInFromDateTime)
          }
        ),
      amount: Yup.string()
        .when('action', {
          is: (value) => !noAmountList.includes(value),
          then: Yup.string()
            .required(intl.formatMessage(captions.isRequired))
            .test(
              'amount',
              formattedMessage.amountError,
              (value) => getDividedAmount(parseStringNumber(value), currency) > 0
            )
        })
        .when('action', {
          is: (value) => value === 'REFUND' || value === 'NO_SHOW',
          then: Yup.string().test(
            'is-within-range',
            intl.formatMessage(
              {
                id: 't.app.orders.operation.popup.input.error',
                defaultMessage: 'Enter a number between {num1} and {num2}'
              },
              {
                num1: 0,
                num2: formatNumberString(getDividedAmount(Number(data?.amount), currency).toString()) || ''
              }
            ),
            (value) => (data?.amount ? parseStringNumber(formatNumberString('' + value)) <= Number(data?.amount) : true)
          )
        })
        .when('action', {
          is: (value) => value === 'DISCOUNT',
          then: Yup.string().test(
            'is-greater-than-total-billed',
            intl.formatMessage(
              {
                id: 't.app.booking.actions.discount.valid',
                defaultMessage: 'The amount must not exceed the total billed'
              }
            ),
            function (value) { 
              const totalBilledAmount = selectedBooking?.reservationLogs?.totalBilledAmount || 0
              const amountValue = parseStringNumber(formatNumberString('' + value))
              return amountValue <= totalBilledAmount
            }
          ) }),
      time: Yup.string()
        .nullable()
        .test(
          'custom-validation',
          intl.formatMessage({
            id: 't.app.bookingManagement.createBookingPayByLink.validator.validTime',
            defaultMessage: 'Enter a valid time'
          }),
          function (value) {
            return value ? moment(value).isValid() : true
          }
        )
        .when('action', {
          is: (value) => value === 'CHECKIN' || value === 'CHECKOUT',
          then: Yup.string().test(
            'checkin-checkout-time-not-in-future',
            intl.formatMessage({
              id: 't.app.bookingManagement.createBookingPayByLink.validator.notFutureDateTime',
              defaultMessage: 'Date and time cannot be in the future'
            }),
            function (value) {
              const date = this?.parent?.date

              if (!date || !value) return true

              const selectedDate = moment(date, formatDate)
              const selectedTime = moment(value)

              if (!selectedDate.isValid() || !selectedTime.isValid()) return true

              const selectedDateTime = selectedDate
                .clone()
                .hour(selectedTime.hour())
                .minute(selectedTime.minute())
                .second(selectedTime.second())
                .millisecond(selectedTime.millisecond())
              return selectedDateTime.isSameOrBefore(moment())
            }
          )
        })
        .when('action', {
          is: (value) => value === 'CHECKOUT',
          then: Yup.string().test(
            'checkout-not-before-checkin-from',
            intl.formatMessage({
              id: 't.app.bookingManagement.createBookingPayByLink.validator.checkoutBeforeCheckinFrom',
              defaultMessage: 'Date and time must not be before check-in date'
            }),
            function (value) {
              const date = this?.parent?.date
              const checkInFrom = selectedBooking?.checkInFrom

              if (!date || !value || !checkInFrom) return true

              const selectedDate = moment(date, formatDate)
              const selectedTime = moment(value)
              const checkInFromDateTime = moment(checkInFrom)

              if (!selectedDate.isValid() || !selectedTime.isValid() || !checkInFromDateTime.isValid()) return true

              const selectedDateTime = selectedDate
                .clone()
                .hour(selectedTime.hour())
                .minute(selectedTime.minute())
                .second(selectedTime.second())
                .millisecond(selectedTime.millisecond())
              return selectedDateTime.isSameOrAfter(checkInFromDateTime)
            }
          )
        }),
      externalPaymentChannel: Yup.string().when('action', {
        is: (value) => value === 'EXTERNAL_REFUND' || value === 'EXTERNAL_PAYMENT',
        then: Yup.string().required(intl.formatMessage(captions.isRequired))
      }),
      externalPaymentChannelDetails: Yup.string().when('externalPaymentChannel', {
        is: (value) => value === 'OTHER',
        then: Yup.string().required(intl.formatMessage(captions.isRequired))
      })
    })
  })
  const selectedAction = data?.action

  // SEND LINK CASE:
  useEffect(() => {
    if (selectedAction === 'SEND_LINK') {
      actions.doSetCreateBookingResult(selectedBooking)
      actions.doSetEventType(selectedBooking?.status === 'AWAITING_FOR_CONFIRMATION' ? 'PAYBYLINK' : 'CARD_SUBSTITUTION_LINK')
      navigate(routesList.bookingManagement.goToCreateBookingPayByLinkResult)
    }
  }, [])

  useEffect(() => {
    formik.setFieldValue('action', data?.action)
    setNoShowExceptionAccepted(false)
  }, [data?.action])

  const handleChangeAmount = (name, value) => {
    if (!/^[0-9.,]*$/.test(value)) return
    if (amountValidator(value)) {
      formik.setFieldValue(name, value)
    }
  }

  const handleBlurAmount = (name, value) => {
    formik.setFieldValue(name, formatNumberString(value))
    formik.setFieldTouched(name, true)
  }

  const getSubtitle = () => {
    switch (selectedAction) {
    case 'CLONE':
      return formattedMessage.cloneDescription
    case 'CARD_SUBSTITUTION_LINK':
      return formattedMessage.cardSubstitutionLinkDescription
    default:
      return ''
    }
  }
 
  // ELEMENTS
  const getData = () => {
    if (selectedAction && !noDateTimeList.includes(selectedAction)) {
      return (
        <div className='amount-field'>
          <DatePicker
            placeholder1={formatDate}
            maxDate={datePickerEternalMaxDate}
            id1='BookingDetailActionsPopUp_Date'
            locale={getLanguageCalendar()}
            onChange1={(date) => {
              formik.setFieldValue('date', date, true)
              formik.setFieldTouched('date', true, false)
            }}
            value1={formik?.values?.date || ''}
            label1={intl.formatMessage(captions.date)}
            ariaLabelCalendarButton={intl.formatMessage(captions.calendar)}
            error1={!!formik?.touched?.date && !!formik?.errors?.date}
            errorMessage1={formik?.errors?.date}
          />  
        </div>
      )
    }
  }
  const getTime = () => {
    if (selectedAction && !noDateTimeList.includes(selectedAction)) {
      return (
        <div className='time-field'>
          <MerchantTimePicker
            label={selectedAction === 'CANCEL' ? formattedMessage.cancelTime : formattedMessage.time}
            value={isNaN(formik?.values?.time) ? null : new Date(formik?.values?.time)}
            onChange={(newValue) => formik.setFieldValue('time', moment(newValue))}
            onBlur={() => formik.setFieldTouched('time', true)}
            name='actionsPopUpTime'
            error={!!formik?.errors?.time && !!formik?.touched?.time ? formik?.errors?.time : null}
          />
        </div>
      )
    }
  }
  const getAmount = () => {
    if (selectedAction && !noAmountList.includes(selectedAction)) {
      return (
        <div className='amount-field'>
          <PortalTextInput
            ariaLabelIcon={intl.formatMessage(messages.warningAriaLabel)} 
            id='BookingDetailActionsPopUp_amount'
            label={intl.formatMessage(captions.amount)}
            value={formik.values.amount}
            onChange={(e) => handleChangeAmount('amount', e.target.value)}
            onBlur={(e) => handleBlurAmount('amount', e.target.value)}
            placeholderBlank
            error={formik?.errors?.amount && formik?.touched?.amount}
            errorText={formik?.errors?.amount}
            onKeyDown={handleOnlyNumberKeyDown}
          />
        </div>
      )
    }
  }
  const getDescriptions = () => {
    if (selectedAction && !noDescriptionList.includes(selectedAction)) {
      return (
        <div className='description_field'>
          <PortalTextInput
            id='BookingDetailActionsPopUp_Description'
            label={intl.formatMessage(captions.description)}
            value={formik.values.description}
            onChange={(e) => formik.setFieldValue('description', e.target.value)}
            placeholderBlank
            maxLength={255}
            multiline
            helperText={`${255 - (formik.values.description?.length || 0)} ${intl.formatMessage(
              (formik.values.description?.length || 0) === 254
                ? captions.remainingCharacter
                : captions.remainingCharacters
            )}`}
          />
        </div>
      )
    }
  }
  const getRadioBox = () => {
    if (selectedAction && selectedAction === 'BILL' && status !== 'AWAITING_FOR_CONFIRMATION') {
      return (
        <div className='bill_radiobox_field'>
          <PortalRadioBox
            options={[
              {
                value: 'BILL_NOW',
                id: 'BILL_NOW',
                label: 'Bill now'
              },
              {
                value: 'BILL_LATER',
                id: 'BILL_LATER',
                label: 'Bill later'
              }
            ]}
            defaultValue={formik.values.billType}
            onChange={(e) => {
              formik.setFieldValue('billType', e)
            }}
            row
          />
        </div>
      )
    }
  }

  const getSelect = () => {
    if (selectedAction && (selectedAction === 'EXTERNAL_REFUND' || selectedAction === 'EXTERNAL_PAYMENT')) {
      return (
        <>
          <PortalSelect
            {...formik.getFieldProps('externalPaymentChannel')}
            id='BookingDetailActionsPopUp_Select'
            label={formattedMessage.externalPaymentChannel}
            options={selectList.map((item) => ({ value: item.value, label: item.label }))}
            onChange={(el) => formik.setFieldValue('externalPaymentChannel', el?.value || '')}
            noSearch
            placeholderBlank
            onBlur={() => {
              formik.setFieldTouched('externalPaymentChannel', true)
            }}
            error={!!formik?.errors?.externalPaymentChannel && !!formik?.touched?.externalPaymentChannel}
            errorText={formik?.errors?.externalPaymentChannel}
          />
          {formik.values.externalPaymentChannel === 'OTHER' && (
            <PortalTextInput // TODO: use getDescriptions() with props instead!!!!
              ariaLabelIcon={intl.formatMessage(messages.warningAriaLabel)} 
              id='BookingDetailActionsPopUp_externalPaymentChannelDetails'
              label={intl.formatMessage(captions.externalPaymentChannelDetails)}
              value={formik.values.externalPaymentChannelDetails}
              onChange={(e) => formik.setFieldValue('externalPaymentChannelDetails', e.target.value)}
              placeholderBlank
              maxLength={255}
              multiline
              helperText={`${255 - (formik.values.externalPaymentChannelDetails?.length || 0)} ${intl.formatMessage(
                (formik.values.externalPaymentChannelDetails?.length || 0) === 244
                  ? captions.remainingCharacter
                  : captions.remainingCharacters
              )}`}
              onBlur={() => formik.setFieldTouched('externalPaymentChannelDetails', true)}
              error={formik?.errors?.externalPaymentChannelDetails && formik?.touched?.externalPaymentChannelDetails}
              errorText={formik?.errors?.externalPaymentChannelDetails}
            />
          )}
        </>
      )
    }
  }

  const actionHandler = () => {
    switch (selectedAction) {
    case 'CLONE':
      window.sessionStorage.setItem('booking_info', JSON.stringify({ id: selectedBooking.id, type: selectedAction }))
      navigate(routesList.bookingManagement.goToCreateBookingPayByLink)
      actions.closePopup()
      break

    default: {
      const values = { ...formik.values }
      values.amount = values?.amount ? parseStringNumber(values.amount) : ''
      const formattedDate = moment(values.date, formatDate)
      values.date = 
          values?.date && values?.time && !noDateTimeList.includes(selectedAction)
            ? joinDateAndTime(formattedDate, values?.time)
            : ''
      values.time = ''
      values.action = ''
      if (selectedAction !== 'BILL') values.billType = ''
      actions.doExecuteBookingAction({ values: { ...values }, url: data?.link || '', action: data?.action })
      break
    }
    }
  }

  return (
    <Toggle toggle={selectedAction === 'SEND_LINK'}>
      <MerchantModal
        bigPopup
        onClose={() => actions.closePopup()}
        title={`${intl.formatMessage(captions.booking)} ${selectedBooking?.id || ''} - ${intl.formatMessage(
          captions[data.action]
        )}`}
        description={getSubtitle()}
        primaryButtonLabel={formattedMessage.confirm}
        primaryAction={() => actionHandler()}
        primaryButtonLoading={isLoading}
        primaryButtonDisabled={
          isLoading || !formik.isValid || (isNoShowOutside72HourWindow && !noShowExceptionAccepted)
        }>
        <div className='popup-action-form-container'>
          <div className='first_group'>
            {getData()}
            {getTime()}
            {getAmount()}
          </div>
          <div className='second_group'>
            {getDescriptions()}
            {isNoShowOutside72HourWindow && (
              <>
                <p>{intl.formatMessage(messages.noShow72HourWarning)}</p>
                <PortalCheckBox
                  options={[
                    {
                      id: 'bookingDetailActionsPopUp_noShowException',
                      label: intl.formatMessage(messages.noShowExceptionAcceptance),
                      checked: noShowExceptionAccepted
                    }
                  ]}
                  onChange={(option) => setNoShowExceptionAccepted(!!option?.checked)}
                />
              </>
            )}
            {getRadioBox()}
            {getSelect()}
          </div>
          {selectedAction && !noDescriptionList.includes(selectedAction) && !noRequiredList.includes(selectedAction) && (
            <span className='mandatoryField'>{formattedMessage.mandatoryField}</span>
          )}
        </div>
      </MerchantModal>
    </Toggle>
  )
}

BookingDetailActionsPopUp.propTypes = {
  actions: PropTypes.object,
  isLoading: PropTypes.bool,
  data: PropTypes.object,
  selectedBooking: PropTypes.object
}

BookingDetailActionsPopUp.defaultProps = {
  actions: {},
  isLoading: false,
  data: {},
  selectedBooking: {}
}

export default BookingDetailActionsPopUp
